import { IPublicClientApplication, PopupRequest, RedirectRequest } from "@azure/msal-browser";
import { fetchUserProfile } from "../api/api";

/**
 * Shared login handler that works for both popup and redirect flows
 * Handles container vs localhost detection automatically
 */
export const handleLogin = async (
  instance: IPublicClientApplication,
  accounts: any[],
  setUserProfile: (profile: any) => void,
  navigate: (path: string) => void
): Promise<void> => {
  // If accounts exist, try to get user profile
  if (accounts.length > 0) {
    const profile = await fetchUserProfile();
    if (profile) {
      setUserProfile(profile);
      navigate("/home");
      return;
    }
    throw new Error("Failed to authenticate. Please try again.");
  }

  // No accounts - initiate login
  const isContainer = !window.location.hostname.includes('localhost');
  
  // Container: use redirect (more reliable)
  // Localhost: try popup first (better UX), fallback to redirect
  if (isContainer) {
    await instance.loginRedirect({
      scopes: [import.meta.env.VITE_SCOPE],
    } as RedirectRequest);
    return; // Redirect navigates away
  }

  // Localhost: try popup first
  try {
    const popupResult = await instance.loginPopup({
      scopes: [import.meta.env.VITE_SCOPE],
    } as PopupRequest);
    
    // After successful popup login, fetch profile
    const profile = await fetchUserProfile();
    if (profile) {
      setUserProfile(profile);
      navigate("/home");
    } else {
      throw new Error("Login successful but failed to fetch profile. Please refresh.");
    }
  } catch (popupError: any) {
    // If popup fails, fall back to redirect
    const shouldFallbackToRedirect = 
      popupError.errorCode === "popup_window_error" || 
      popupError.errorCode === "user_cancelled" ||
      popupError.errorCode === "popup_timeout" ||
      popupError.errorCode === "interaction_required" ||
      popupError.message?.includes("popup") ||
      popupError.message?.includes("blocked") ||
      popupError.message?.includes("timeout") ||
      popupError.message?.includes("blank");

    if (shouldFallbackToRedirect) {
      await instance.loginRedirect({
        scopes: [import.meta.env.VITE_SCOPE],
      } as RedirectRequest);
      return; // Redirect navigates away
    }
    
    // Re-throw other errors
    throw popupError;
  }
};

